import { useQueryClient } from '@tanstack/react-query';
import { Select } from 'antd';

import { Badge } from '@/components/Badge/Badge';
import Input from '@/components/Inputs/Input';
import Modal, { useModalContext } from '@/components/Modal/Modal';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import { getApiErrorMessage } from '@/error-handler/api-error-messages/ApiErrorMessages';
import { ApiError } from '@/error-handler/ApiError';
import { useStructuredStorageFilterQuery } from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import useModalState from '@/hooks/pure/useModalState';
import useStates from '@/hooks/pure/useStates';
import { popup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { productInfoService } from '../../api';
import useProductStore from '../../store/ProductStore';
import type { ProductHierarchy, ProductOption, ProductT } from '../../types';
import { getProductByBrand, getProductByLine } from '../../utils/product.utils';
import { DeleteModal } from '../DeleteItem/DeleteModal';

export const DetailModal = (props: {
  alias: string;
  type: ProductHierarchy;
}) => {
  const { alias, type } = props;
  const queryClient = useQueryClient();
  const productMap = useProductStore((state) => state.groupedProductMap);
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const productOptionMap = useProductStore((state) => state.productOptionMap);
  const { clearFilter } = useStructuredStorageFilterQuery(
    STORAGE_KEYS.FEEDBACK_FILTER_STATUS_KEY,
  );
  const clearCache = () => {
    clearFilter();
    queryClient.invalidateQueries();
  };
  switch (type) {
    case 'product': {
      const product = productMap.get(alias);
      if (!product) {
        throw new Error(`Product not found: ${alias}`);
      }
      return <ProductDetail product={product} clearCache={clearCache} />;
    }
    case 'brand': {
      const brand = brandMap.get(alias);
      if (!brand) {
        throw new Error(`Brand not found: ${alias}`);
      }
      const products = getProductByBrand(productMap, alias);
      return (
        <BrandDetail
          brand={brand}
          products={products}
          clearCache={clearCache}
        />
      );
    }

    case 'line': {
      const line = lineMap.get(alias);
      if (!line) {
        throw new Error(`Line not found: ${alias}`);
      }
      const products = getProductByLine(productMap, alias);
      return (
        <LineDetail line={line} products={products} clearCache={clearCache} />
      );
    }
    case 'option': {
      const option = productOptionMap.get(alias);
      if (!option) {
        throw new Error(`Option not found: ${alias}`);
      }
      return <OptionDetail option={option} clearCache={clearCache} />;
    }
    default:
      throw new Error(`Invalid type: ${type}`);
  }
};

const ProductDetail = (props: {
  product: ProductT;
  clearCache: () => void;
}) => {
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const { closeModal } = useModalContext();
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const deleteModalState = useModalState();
  const { product, clearCache } = props;
  const { state, setValue } = useStates({
    input: product.product_name || '',
    brand: product.brand_id,
    line: product.line_id,
  });

  const onSave = async () => {
    try {
      await productInfoService.updateProduct(product.alias, {
        name: state.input,
        brand_id: state.brand,
        line_id: state.line,
      });
      await fetchProductList(getCurrWorkspaceAlias());
      clearCache();
      toast('Product updated', 'success');
      await closeModal();
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.data.client_code === 'product__duplicate_name') {
          const duplicateError = new ApiError<{ product_id: string }>(error);
          const duplicateProduct = duplicateError.data.data.product_id;
          if (!duplicateProduct) throw new Error('Duplicate product not found');
          popup({
            type: 'confirm',
            popupTheme: 'negative',
            title: 'A product with the same name exists',
            message: (
              <>
                A product with the same name already exists at this level. If
                you continue, the selected product will be merged with the
                existing one.
                <br />
                <br />
                The selected product will be deleted, and its associated
                feedback will be moved to the existing product.
              </>
            ),
            onConfirm: async () => {
              await productInfoService.mergeProducts({
                from: [product.alias],
                to: duplicateProduct,
              });
              await fetchProductList(getCurrWorkspaceAlias());
              await closeModal();
              clearCache();
            },
          });
        } else {
          toast(getApiErrorMessage(error.data.client_code), 'fail');
        }
      }
    }
  };

  return (
    <Modal.Layout
      className="max-h-[calc(100vh-64px)] overflow-auto"
      modalSize="m"
    >
      <Modal.Header>
        <Modal.Title>{product.product_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto">
        <div className="grid gap-2 grid-cols-[100px_1fr] grid-rows-[40px_40px_40px_40px_auto]">
          <div>Type</div>
          <div>Product</div>
          <div>Name</div>
          <div>
            <Input
              sizeType="m"
              value={state.input}
              onChange={(e) => setValue('input', e.target.value)}
            />
          </div>
          <div>Brand</div>
          <div>
            <Select
              className="w-full"
              value={state.brand}
              options={brandMap
                .values()
                .toArray()
                .map((brand) => ({
                  label: brand.name,
                  value: brand.id,
                }))}
              onChange={(value) => setValue('brand', value)}
            />
          </div>
          <div>Line</div>
          <div>
            <Select
              className="w-full"
              value={state.line}
              options={lineMap
                .values()
                .toArray()
                .map((line) => ({
                  label: line.name,
                  value: line.id,
                }))}
              onChange={(value) => setValue('line', value)}
            />
          </div>
          <div>Options</div>
          <div className="flex flex-wrap gap-2">
            {product.options?.map((option) => {
              return (
                <Badge key={option.id} variant="default">
                  {option.option_name}
                </Badge>
              );
            })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        additional={{
          children: 'Delete',
          variant: 'error_outlined',
          onClick: () => deleteModalState.open(),
        }}
        cancel={{
          children: 'Cancel',
          onClick: closeModal,
          variant: 'secondary',
        }}
        confirm={{
          children: 'Save',
          variant: 'primary',
          onClick: onSave,
        }}
      />
      <Modal
        status={deleteModalState.status}
        closeModal={deleteModalState.close}
      >
        <DeleteModal
          target="product"
          targetId={product.alias}
          onDeleteConfirmCallback={closeModal}
        />
      </Modal>
    </Modal.Layout>
  );
};

const BrandDetail = (props: {
  brand: ProductBrandOption;
  products: {
    type: string;
    id: string;
    name?: string;
  }[];
  clearCache: () => void;
}) => {
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const deleteModalState = useModalState();
  const { closeModal } = useModalContext();
  const { brand, products, clearCache } = props;
  const { state, setValue } = useStates({
    input: brand.name || '',
  });
  const onSave = async () => {
    try {
      await productInfoService.updateBrand(brand.id, {
        name: state.input,
      });
      await fetchProductList(getCurrWorkspaceAlias());
      toast('Brand updated', 'success');
      clearCache();
      await closeModal();
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.data.client_code === 'product__duplicate_name') {
          const duplicateError = new ApiError<{ brand_id: string }>(error);
          const duplicateBrand = duplicateError.data.data.brand_id;
          if (!duplicateBrand) throw new Error('Duplicate brand not found');
          popup({
            type: 'confirm',
            popupTheme: 'negative',
            title: 'A brand with the same name exists',
            message: (
              <>
                A brand with the same name already exists. If you continue, the
                selected brand will be merged with the existing one.
                <br />
                <br />
                The selected brand will be deleted, and its associated feedback
                will be moved to the existing brand.
              </>
            ),
            onConfirm: async () => {
              await productInfoService.mergeBrands({
                from: [brand.id],
                to: duplicateBrand,
              });
              await fetchProductList(getCurrWorkspaceAlias());
              await closeModal();
              clearCache();
            },
          });
        } else {
          toast(getApiErrorMessage(error.data.client_code), 'fail');
        }
      }
    }
  };
  return (
    <Modal.Layout
      className="max-h-[calc(100vh-64px)] overflow-auto"
      modalSize="m"
    >
      <Modal.Header>
        <Modal.Title>{brand.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto">
        <div className="grid gap-2 grid-cols-[100px_1fr] grid-rows-[40px_40px_40px_40px_auto]">
          <div>Type</div>
          <div>Brand</div>
          <div>Name</div>
          <div>
            <Input
              sizeType="m"
              value={state.input}
              onChange={(e) => setValue('input', e.target.value)}
            />
          </div>
          <div>Products</div>
          <div className="flex flex-wrap gap-2">
            {products.map((product) => {
              return (
                <Badge key={product.id} variant="default">
                  {product.name}
                </Badge>
              );
            })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        additional={{
          children: 'Delete',
          variant: 'error_outlined',
          onClick: () => deleteModalState.open(),
        }}
        cancel={{
          children: 'Cancel',
          onClick: closeModal,
          variant: 'secondary',
        }}
        confirm={{
          children: 'Save',
          variant: 'primary',
          onClick: onSave,
        }}
      />
      <Modal
        status={deleteModalState.status}
        closeModal={deleteModalState.close}
      >
        <DeleteModal
          target="brand"
          targetId={brand.id}
          onDeleteConfirmCallback={closeModal}
        />
      </Modal>
    </Modal.Layout>
  );
};

const LineDetail = (props: {
  line: ProductLineOption;
  products: {
    type: string;
    id: string;
    name?: string;
  }[];
  clearCache: () => void;
}) => {
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const { line, products, clearCache } = props;
  const deleteModalState = useModalState();
  const { closeModal } = useModalContext();
  const { state, setValue } = useStates({
    input: line.name || '',
  });
  const onSave = async () => {
    try {
      await productInfoService.updateLine(line.id, {
        name: state.input,
      });
      await fetchProductList(getCurrWorkspaceAlias());
      toast('Line updated', 'success');
      clearCache();
      await closeModal();
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.data.client_code === 'product__duplicate_name') {
          const duplicateError = new ApiError<{ line_id: string }>(error);
          const duplicateLine = duplicateError.data.data.line_id;
          if (!duplicateLine) throw new Error('Duplicate line not found');
          popup({
            type: 'confirm',
            popupTheme: 'negative',
            title: 'A line with the same name exists',
            message: (
              <>
                A line with the same name already exists. If you continue, the
                selected line will be merged with the existing one.
                <br />
                <br />
                The selected line will be deleted, and its associated feedback
                will be moved to the existing line.
              </>
            ),
            onConfirm: async () => {
              await productInfoService.mergeLines({
                from: [line.id],
                to: duplicateLine,
              });
              await fetchProductList(getCurrWorkspaceAlias());
              await closeModal();
              clearCache();
            },
          });
        } else {
          toast(getApiErrorMessage(error.data.client_code), 'fail');
        }
      }
    }
  };
  return (
    <Modal.Layout
      className="max-h-[calc(100vh-64px)] overflow-auto"
      modalSize="m"
    >
      <Modal.Header>
        <Modal.Title>{line.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto">
        <div className="grid gap-2 grid-cols-[100px_1fr] grid-rows-[40px_40px_40px_40px_auto]">
          <div>Type</div>
          <div>Line</div>
          <div>Name</div>
          <div>
            <Input
              sizeType="m"
              value={state.input}
              onChange={(e) => setValue('input', e.target.value)}
            />
          </div>
          <div>Products</div>
          <div className="flex flex-wrap gap-2">
            {products.map((product) => {
              return (
                <Badge key={product.id} variant="default">
                  {product.name}
                </Badge>
              );
            })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        additional={{
          children: 'Delete',
          variant: 'error_outlined',
          onClick: () => deleteModalState.open(),
        }}
        cancel={{
          children: 'Cancel',
          onClick: closeModal,
          variant: 'secondary',
        }}
        confirm={{
          children: 'Save',
          variant: 'primary',
          onClick: onSave,
        }}
      />
      <Modal
        status={deleteModalState.status}
        closeModal={deleteModalState.close}
      >
        <DeleteModal
          target="line"
          targetId={line.id}
          onDeleteConfirmCallback={closeModal}
        />
      </Modal>
    </Modal.Layout>
  );
};

const OptionDetail = (props: {
  option: ProductOption;
  clearCache: () => void;
}) => {
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const { option, clearCache } = props;
  const productMap = useProductStore((state) => state.groupedProductMap);
  const deleteModalState = useModalState();
  const { closeModal } = useModalContext();
  const { state, setValue } = useStates({
    input: option.option_name || '',
    product: option.product_alias,
  });
  const onSave = async () => {
    try {
      await productInfoService.updateProductOption(option.id, {
        name: state.input,
        product_id: state.product,
      });
      await fetchProductList(getCurrWorkspaceAlias());
      await closeModal();
      clearCache();
      toast('Product option updated', 'success');
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.data.client_code === 'product__duplicate_name') {
          const duplicateError = new ApiError<{ product_option_id: string }>(
            error,
          );
          const duplicateProductOption =
            duplicateError.data.data.product_option_id;
          if (!duplicateProductOption)
            throw new Error('Duplicate product option not found');
          popup({
            type: 'confirm',
            popupTheme: 'negative',
            title: 'A product option with the same name exists',
            message: (
              <>
                A option with the same name already exists at this level. If you
                continue, the selected option will be merged with the existing
                one.
                <br />
                <br />
                The selected option will be deleted, and its associated feedback
                will be moved to the existing option.
              </>
            ),
            onConfirm: async () => {
              await productInfoService.mergeProductOptions({
                from: [option.id],
                to: duplicateProductOption,
              });
              await fetchProductList(getCurrWorkspaceAlias());
              await closeModal();
              clearCache();
            },
          });
        } else {
          toast(getApiErrorMessage(error.data.client_code), 'fail');
        }
      }
    }
  };
  return (
    <Modal.Layout
      className="max-h-[calc(100vh-64px)] overflow-auto"
      modalSize="m"
    >
      <Modal.Header>
        <Modal.Title>{option.option_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto">
        <div className="grid gap-2 grid-cols-[100px_1fr] grid-rows-[40px_40px_40px_40px_auto]">
          <div>Type</div>
          <div>Option</div>
          <div>Name</div>
          <div>
            <Input
              sizeType="m"
              value={state.input}
              onChange={(e) => setValue('input', e.target.value)}
            />
          </div>
          <div>Product</div>
          <div>
            <Select
              value={state.product}
              options={productMap
                .values()
                .toArray()
                .map((product) => ({
                  label: product.product_name,
                  value: product.alias,
                }))}
              onChange={(value) => setValue('product', value)}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        additional={{
          children: 'Delete',
          variant: 'error_outlined',
          onClick: () => deleteModalState.open(),
        }}
        cancel={{
          children: 'Cancel',
          onClick: closeModal,
          variant: 'secondary',
        }}
        confirm={{
          children: 'Save',
          variant: 'primary',
          onClick: onSave,
        }}
      />
      <Modal
        status={deleteModalState.status}
        closeModal={deleteModalState.close}
      >
        <DeleteModal
          target="option"
          targetId={option.id}
          onDeleteConfirmCallback={closeModal}
        />
      </Modal>
    </Modal.Layout>
  );
};
